import { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import {
    InputGroup,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
} from "../../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import "react-datepicker/dist/react-datepicker.css";
import {
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
} from "@mui/material";
import DropZone from "./MediaDropZone";

/* ---------------- CONSTANTS ---------------- */

const MEDIA_TYPES = [
    { value: 'Bedroom', label: 'Bedroom' },
    { value: 'Bathroom', label: 'Bathroom' },
    { value: 'Kitchen', label: 'Kitchen' },
    { value: 'Hall', label: 'Hall' },
    { value: 'Outside', label: 'Outside' },
    { value: 'Locality', label: 'Locality' },
];

/* ---------------- VALIDATION ---------------- */

const mediaSchema = Yup.object().shape({
    mediaType: Yup.string().required("Media type is required"),
    file: Yup.array().min(1, "At least one file is required"),
});

const PropertyEditMediaModal = ({
    setIsMediaEdit,
    uploadMediaMutate,
    isMediaLoading,
    propId,
    selectedData
}) => {

    /* ---------------- HELPERS ---------------- */

    const createFileFromUrl = async (url, filename) => {
        try {
            const res = await fetch(url);
            const blob = await res.blob();
            return new File([blob], filename, { type: blob.type });
        } catch (e) {
            return null;
        }
    };

    /* ---------------- STATE ---------------- */

    const [mediaByType, setMediaByType] = useState({});
    const [selectedMediaType, setSelectedMediaType] = useState("Bedroom");
    const [initialFile, setInitialFile] = useState([]);
    const [formKey, setFormKey] = useState(Date.now());
    const [loading, setLoading] = useState(false);

    /* ---------------- GROUP DATA ---------------- */

    useEffect(() => {
        if (selectedData?.length) {
            const grouped = {};

            selectedData.forEach(item => {
                if (!grouped[item.title]) {
                    grouped[item.title] = [];
                }
                grouped[item.title].push(item);
            });

            setMediaByType(grouped);

            // default select first type
            setSelectedMediaType(selectedData[0]?.title || "Bedroom");
        }
    }, [selectedData]);

    /* ---------------- LOAD FILES ---------------- */

    useEffect(() => {
        const loadFiles = async () => {
            const list = mediaByType[selectedMediaType];

            if (!list) {
                setInitialFile([]);
                return;
            }

            setLoading(true);

            const files = await Promise.all(
                list.map(async (item) => {
                    const filename = item.url.split('/').pop();
                    const file = await createFileFromUrl(item.url, filename);

                    if (file) {
                        Object.defineProperty(file, 'preview', {
                            value: item.url,
                            writable: false
                        });
                    }

                    return file;
                })
            );

            setInitialFile(files.filter(Boolean));
            setFormKey(Date.now());
            setLoading(false);
        };

        loadFiles();
    }, [selectedMediaType, mediaByType]);

    /* ---------------- SUBMIT ---------------- */

    const handleSubmit = (values) => {
        const formData = new FormData();

        formData.append("type", values.mediaType);
        formData.append("propId", propId);

        const existingList = mediaByType[values.mediaType] || [];

        // keep old files
        existingList.forEach(item => {
            formData.append("existingFiles", item.url);
        });

        // add new files
        values.file.forEach(file => {
            const isExisting = existingList.some(i => i.url === file.preview);
            if (!isExisting) {
                formData.append("files", file);
            }
        });

        uploadMediaMutate(formData);
    };

    /* ---------------- UI ---------------- */

    return (
        <Container>
            <Header>
                <Title>Update Media</Title>
                <button onClick={() => setIsMediaEdit(false)}>
                    <MdClose color="white" size={24} />
                </button>
            </Header>

            <div className="px-6 flex flex-col justify-between h-full">

                <Formik
                    key={formKey}
                    enableReinitialize
                    initialValues={{
                        mediaType: selectedMediaType,
                        file: initialFile,
                    }}
                    validationSchema={mediaSchema}
                    onSubmit={handleSubmit}
                >
                    {(formik) => {

                        const { values, setFieldValue, errors, touched } = formik;

                        return (
                            <Form>
                                <ScrollableContent>

                                    <Wrapper>

                                        {/* MEDIA TYPE */}
                                        <InputGroup>
                                            <Label>Media Type *</Label>

                                            <FormControl
                                                fullWidth
                                                error={touched.mediaType && Boolean(errors.mediaType)}
                                            >
                                                <Select
                                                    value={values.mediaType}
                                                    onChange={(e) => {
                                                        const type = e.target.value;
                                                        setSelectedMediaType(type);
                                                        setFieldValue("mediaType", type);
                                                    }}
                                                >
                                                    {MEDIA_TYPES.map(item => (
                                                        <MenuItem key={item.value} value={item.value}>
                                                            {item.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>

                                                {errors.mediaType && (
                                                    <FormHelperText>{errors.mediaType}</FormHelperText>
                                                )}
                                            </FormControl>
                                        </InputGroup>

                                        {/* DROPZONE */}
                                        <InputGroup>
                                            <Label>Upload Media *</Label>

                                            <Field name="file">
                                                {(props) => (
                                                    <DropZone
                                                        File={props.field.value || []}
                                                        setFieldValue={props.form.setFieldValue}
                                                        existingImageUrl={
                                                            mediaByType[values.mediaType]?.map(i => i.url) || []
                                                        }
                                                    />
                                                )}
                                            </Field>

                                            {errors.file && touched.file && (
                                                <ErrorMessage>{errors.file}</ErrorMessage>
                                            )}
                                        </InputGroup>

                                    </Wrapper>
                                </ScrollableContent>

                                <ActionSection>
                                    <SideCancelBtn
                                        type="button"
                                        onClick={() => setIsMediaEdit(false)}
                                    >
                                        Cancel
                                    </SideCancelBtn>

                                    <SubmitBtn type="submit" disabled={isMediaLoading}>
                                        {isMediaLoading ? "Updating..." : "Update"}
                                    </SubmitBtn>
                                </ActionSection>

                            </Form>
                        );
                    }}
                </Formik>

            </div>
        </Container>
    );
};

/* ---------------- STYLES ---------------- */

const Header = tw.div`flex items-center justify-between bg-[#0078BD] py-4 px-4`;
const Container = tw.div`bg-white w-full h-full flex flex-col`;
const Title = tw.h1`text-lg font-medium text-white`;
const ErrorMessage = tw.div`text-red-500 text-xs mt-1`;

export default PropertyEditMediaModal;